import _ from 'lodash';
import { useState, useEffect, useMemo } from "react";
import { useMsal, } from '@azure/msal-react';
import { useNavigate } from "react-router-dom";
import { NotificationsAPI } from "../../../api/notifications/notifications-api-factory";
import Humanizer from "humanize-duration";
import { DateTime } from "luxon";
import { HeartBeat } from './heartbeat';
import { useSignalR } from "../../../app";
import useTrackedProperty from "../../../app/useTrackedProperty";

import './notifications.css';
import { arrayAny } from '../../../utils/arrayUtils';

export const NotificationsDropDown = ({ location }) => {

	const { instance, inProgress, accounts } = useMsal();
	const navigate = useNavigate();

	const [rerender, setRerender] = useState(false);
	const [data, setData] = useState(null);
	const [heartBeats, setHeartBeats] = useState(null);
	const [fetchInProgress, setFetchInProgress] = useState(false);
	const [fetchComplete, setFetchComplete] = useState(false);
	const [includeDismissed, setIncludeDismissed] = useState(false);

	const signalRApi = useSignalR();

	const api = useMemo(() => new NotificationsAPI(instance, inProgress, accounts), [instance, inProgress, accounts]);

	useEffect(() => {
		if (!signalRApi) return;

		signalRApi.subscribe("heartbeat", handleWsHeartBeat);
		signalRApi.subscribe("notification", handleWsNotification);
		signalRApi.subscribe("newChatMessage", handleWsNewChatMessage);
		signalRApi.subscribe("groupChange", data => console.log("User Connected:", data));

		return () => {
			// Safe to call even if not previously subscribed
			signalRApi.unsubscribe("heartbeat", handleWsHeartBeat);
			signalRApi.unsubscribe("notification", handleWsNotification);
			signalRApi.unsubscribe("newChatMessage", handleWsNewChatMessage);
			signalRApi.unsubscribe("groupChange", () => {});
		};
	}, [signalRApi]);

	useEffect(() => {

		if (!fetchInProgress && !fetchComplete) {
			setFetchInProgress(true);

			api.getMyNotifications(includeDismissed)
				.then(data => {
					setData(data);
					setFetchComplete(true);
				})
				.catch(error => {
					console.error("An error occurred fetching data.", error)
				})
				.finally(() => {
					setFetchInProgress(false);
				});
		}

	}, [location, api, includeDismissed, fetchInProgress, fetchComplete]);

	const handleWsNotification = notification => {
		setData(current => {
			if (Array.isArray(current)) {
				// Create a new array to ensure React detects the change
				const newData = [...current];
				const existingIndex = _.findIndex(newData, n => n.uniqueIndentifier === notification.uniqueIndentifier);

				if (existingIndex > -1) {
					// Replace existing notification
					newData[existingIndex] = notification;
				} else {
					// Add new notification
					newData.unshift(notification); // Add to beginning of array
				}

				return newData;
			}
			// If current is null or not an array, return new array with notification
			return [notification];
		});
	};


	const handleWsNewChatMessage = message => {
		console.log("Handle Chat Message updates:", message);
	};

	const handleWsHeartBeat = message => {

		const heartBeatNotification = {
			type: "heartbeat",
			companyName: message.user.companyName,
			topic: `${message.user.displayName}'s activity status`,
			icon: message.user.thumbnailProfileImageIncludingSasToken && message.user.thumbnailProfileImageIncludingSasToken.length > 0 ? "fas fa-circle bg-transparent border-0 text-bright-green media-object-icon" : "fa-user",
			id: message.user.id,
			synthetic: true,
			imagePath: message.user.thumbnailProfileImageIncludingSasToken,
			created: message.heartbeat.timestamp,
			links: [{
				displayText: "Go to User's Details",
				link: `/user/${message.user.id}`,
				description: "The landlord the document belongs to",
				containerName: null,
				target: "_self"
			}]
		};

		setHeartBeats(current => {
			setRerender(!rerender);
			if (Array.isArray(current)) {
				// Create a new array to ensure React detects the change
				const newData = [...current];
				const existingIndex = _.findIndex(newData, h => h.id === heartBeatNotification.id);

				if (existingIndex > -1) {
					// Replace existing heartbeat
					newData[existingIndex] = heartBeatNotification;
				} else {
					// Add new heartbeat
					newData.unshift(heartBeatNotification); // Add to beginning of array
				}

				return newData;
			}
			// If current is null or not an array, return new array with heartbeat
			return [heartBeatNotification];
		});

	};

	const dismissNotification = notification => {

		notification.dismissedChannels = [...notification.dismissedChannels, "InAppNotification"];
		console.log("Dismiss Notification", notification);

		api.dismissNotification(notification)
			.then(() => {
				setData(current => {
					const index = _.findIndex(current, n => n.uniqueIndentifier === notification.uniqueIndentifier);
					current.splice(index, 1);
					return [].concat([...current]); // Without this we have a rerender issue!
				});
			})
			.catch(error => console.log("An error occurred dismissing notification", { error, notification }));
	};

	const getDuration = (date, isIso, format) => {

		format ??= "yyyy-MM-dd HH:mm:ss"; // "2024-07-29 15:23:39"
		const now = DateTime.now().toLocal();

		const duration = isIso ?
			now.diff(DateTime.fromISO(date).toLocal()) :
			now.diff(DateTime.fromFormat(date, format, { zone: 'utc' }).toLocal());

		const result = Humanizer(duration, { round: true, largest: 1 })

		return result;
	};

	const getDisplayDate = (date, isIso, format) => {

		if (isIso) {
			const dateConversion = DateTime.fromISO(date).toUTC().toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS);
			return dateConversion;
		}

		format ??= "yyyy-MM-dd HH:mm:ss"; // "2024-07-29 15:23:39"

		date = DateTime.fromFormat(date, format).toUTC().toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS);
		return date;

	};

	const handleLink = link => {

		const r = new RegExp("^(?:[a-z]+:)?//", "i");
		if (r.test(link.link)) {
			// URL is absolute
			window.open(link.link, link.target);
			return;
		} else {
			// URL is relative
			navigate(link.link);
			return;
		}
	};

	const combinedNotifications = [
		...(Array.isArray(data) ? data : []),
		...(Array.isArray(heartBeats) ? heartBeats : [])
	];
	const notificationCount = () => combinedNotifications.length;

	return (
		<div className="navbar-item dropdown">
			<a href="#/" data-bs-toggle="dropdown" className="navbar-link dropdown-toggle icon">
				<i className={`fa fa-bell ${notificationCount() > 0 ? "" : "text-muted"}`}></i>
				{notificationCount() > 0 && <span className="badge">{notificationCount()}</span>}
			</a>
			<div className="dropdown-menu media-list dropdown-menu-end">
				<div className="dropdown-header row">
					<div className="col-md-2 pt-2">
						NOTIFICATIONS ({notificationCount()})
					</div>
					<div className="col-md-10 pt-2">
						<div className="form-check form-switch">
							<label className="form-check-label" htmlFor="allDay">Include Dismissed</label>
							<input className="form-check-input" type="checkbox" id="allDay" checked={includeDismissed} onChange={e => setIncludeDismissed(e.target.checked)} />
						</div>
					</div>
				</div>

				{arrayAny(data) && data.map((n, i) =>
					<div key={`notification-${i}`} className="dropdown-item media">
						<div className="media-left">
							{n.imagePath ? <>
								<img src={n.imagePath} className="media-object" alt=""></img>
								<i className={`fa ${n.icon}`}></i>
							</>
								:
								<>
									<i className={`fa ${n.icon} media-object bg-gray-500`}></i>
								</>
							}

						</div>
						<div className="media-body">
							<h6 className="media-heading text-wrap mb-1">{n.topic} <i className={n.indicator}></i></h6>
							{arrayAny(n.links) && n.links.map((link, i) =>
								<div key={`link-${i}`}>
									<button className="btn btn-blue mb-1" title={link.description} onClick={() => { handleLink(link); }} >
										<i title={link.displayText} className={link.target === "_blank" ? "fa fa-external-link" : "fa fa-arrow-right"}></i>&nbsp;&nbsp;{link.displayText}
									</button>
								</div>
							)}
							{/* {arrayAny(_.filter(n.dismissedChannels, c => c === "inAppNotification")) && !n.synthetic && <button className="btn btn-green mb-1" title="Dismiss" onClick={() => dismissNotification(n)} >
								<i title="Dismiss" className="fa fa-circle-check fa-lg"></i>&nbsp;&nbsp;Dismiss
							</button>} */}

							{!!!n.synthetic && <button className="btn btn-xs btn-green mb-1" title="Dismiss" onClick={() => dismissNotification(n)} >
								<i title="Dismiss" className="fa fa-circle-check fa-lg"></i>&nbsp;&nbsp;Dismiss
							</button>}

							<div title={getDisplayDate(n.created, true, null)} className="text-muted fs-10px">{getDuration(n.created, true, null)} ago</div>
						</div>
					</div>
				)}

				{arrayAny(heartBeats) && heartBeats.map((n, i) =>
					<HeartBeat key={`heartbeat-${i}`} heartBeat={n} getDisplayDate={getDisplayDate} getDuration={getDuration} />
				)}

				{!!data && arrayAny(_.filter(data, n => !Object.hasOwn(n, "synthetic") || (Object.hasOwn(n, "synthetic") && !n.synthetic))) && <div className="dropdown-footer text-center">
					<button className="btn btn-green mb-1" title="Dismiss" onClick={() => console.log("Dismiss All Notification", data)} >
						<i title="Dismiss" className="fa fa-circle-check fa-lg"></i>&nbsp;&nbsp;Dismiss All
					</button>
				</div>}
			</div>
		</div>
	);
};